import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, ArrowRight, X, Check, ChevronRight,
  Receipt, FileText, Zap, Chrome,
  Upload, CreditCard, Sparkles, User,
  Circle, ExternalLink, Info
} from 'lucide-react';

const ONBOARDING_KEY = 'centris_onboarding_v1';
const TOOLTIPS_KEY = 'centris_tooltips_dismissed';
const CHECKLIST_KEY = 'centris_setup_checklist_v1';
export const PROFILE_KEY = 'centris_user_profile';

const BUSINESS_TYPES = [
  { label: 'Freelancer', emoji: '💻' },
  { label: 'Agency', emoji: '🏢' },
  { label: 'Consultant', emoji: '📊' },
  { label: 'E-commerce', emoji: '🛍️' },
  { label: 'Creator', emoji: '🎬' },
  { label: 'Other', emoji: '✨' },
];

const STEPS = [
  {
    id: 'welcome',
    icon: Shield,
    iconColor: 'from-indigo-500 to-purple-600',
    title: 'Welcome to CENTRIS',
    subtitle: "You're in. Let's get you set up in 2 minutes.",
    description: null,
    cta: 'Get Started',
    skip: false,
  },
  {
    id: 'profile',
    icon: User,
    iconColor: 'from-violet-500 to-indigo-600',
    title: 'Tell us about yourself',
    subtitle: "We'll personalize your experience based on how you work.",
    description: null,
    cta: 'Continue',
    skip: true,
    isProfile: true,
  },
  {
    id: 'expenses',
    icon: Receipt,
    iconColor: 'from-pink-500 to-rose-600',
    title: 'Track Your Expenses',
    subtitle: 'Three ways to import - pick what works for you.',
    description: [
      { icon: Upload, label: 'Upload a bank CSV', sub: 'Export from your bank and drop it in' },
      { icon: Receipt, label: 'Scan a receipt', sub: 'Point your camera, AI does the rest' },
      { icon: CreditCard, label: 'Live bank link', sub: 'Coming soon — use CSV upload or receipt scan for now' },
    ],
    cta: 'Next',
    skip: true,
  },
  {
    id: 'invoice',
    icon: FileText,
    iconColor: 'from-purple-500 to-violet-600',
    title: 'Send Your First Invoice',
    subtitle: 'Professional invoices in under 2 minutes.',
    description: [
      { icon: FileText, label: 'Pick a theme', sub: 'Classic, Modern, or Executive' },
      { icon: Zap, label: 'Fill in the details', sub: 'Client name, line items, due date' },
      { icon: ArrowRight, label: 'Send via email', sub: 'Delivered instantly, tracked automatically' },
    ],
    cta: 'Next',
    skip: true,
  },
  {
    id: 'quantra',
    icon: Sparkles,
    iconColor: 'from-cyan-500 to-blue-600',
    title: 'Meet Quantra, Your AI CFO',
    subtitle: 'Ask anything about your finances - in plain English.',
    description: null,
    prompts: [
      'Can I afford to take next month off?',
      'How much should I set aside for taxes?',
      'Am I in a feast or famine cycle right now?',
      'What were my biggest expenses last quarter?',
    ],
    cta: 'Next',
    skip: true,
  },
  {
    id: 'extension',
    icon: Chrome,
    iconColor: 'from-amber-500 to-orange-500',
    title: 'Optional: Chrome Extension',
    subtitle: 'Tracks subscription usage automatically - totally optional.',
    description: null,
    cta: 'Finish Setup',
    skip: true,
    isExtension: true,
  },
];

const TOOLTIP_CONTENT = {
  'extension-pairing': {
    title: 'Pairing the Chrome Extension',
    steps: [
      'Click "Generate Code" - you will get a 6-digit number',
      'Open the CENTRIS extension in Chrome (click its icon in your toolbar)',
      'Paste or type the code into the extension popup',
      'Hit Connect - the status turns green instantly',
    ],
    note: 'The code expires in 10 minutes. If it expires, just generate a new one.',
    optional: true,
    optionalNote: 'This extension is 100% optional. CENTRIS works fully without it. It only adds automatic subscription visit tracking.',
  },
  'receipt-scanner': {
    title: 'Receipt Scanner Tips',
    steps: [
      'Use your phone camera for best results - tap the camera icon',
      'Make sure the receipt is flat and well-lit',
      'The AI reads merchant, amount, date, and category automatically',
      'You can edit any field it gets wrong before saving',
    ],
    note: 'Supports photos and PDF receipts.',
  },
  'bank-csv': {
    title: 'Importing a Bank CSV',
    steps: [
      "Log into your bank's website",
      'Find "Export" or "Download transactions" (usually under Statements or Activity)',
      'Choose CSV format and a date range',
      'Drop the file into CENTRIS - it handles the rest',
    ],
    note: 'Works with most major US banks. Transfers and credit card payments are automatically excluded.',
  },
  'tax-vault': {
    title: 'How the Tax Vault Works',
    steps: [
      'Set your reserve percentage (default 25% - covers self-employment + income tax)',
      'Every time income is logged, CENTRIS calculates what to set aside',
      "The vault tracks your running balance so you're never caught off guard",
      'Export a summary at any time for your accountant',
    ],
    note: "The cushion % adds a buffer above your reserve - recommended if you're in a higher tax bracket.",
  },
  'video-ads': {
    title: 'Generating Video Ads',
    steps: [
      'Describe what you want to promote in the topic box',
      'CENTRIS writes a 5-scene script automatically',
      'Each scene becomes a 5-second video clip via Runway AI',
      'Download all 5 clips - ready for TikTok, Reels, or YouTube Shorts',
    ],
    note: 'Generation takes 2-5 minutes per set. Pro plan gets 5 sets/month, Studio gets 15.',
  },
};

export const useOnboarding = () => {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const done = localStorage.getItem(ONBOARDING_KEY);
    if (!done) {
      const t = setTimeout(() => setShow(true), 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  const finish = useCallback(() => {
    localStorage.setItem(ONBOARDING_KEY, 'done');
    setShow(false);
  }, []);

  const next = useCallback(() => {
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      finish();
    }
  }, [step, finish]);

  const skip = useCallback(() => {
    finish();
  }, [finish]);

  return { show, step, currentStep: STEPS[step], next, finish, skip, totalSteps: STEPS.length };
};

export const getUserProfile = () => {
  try { return JSON.parse(localStorage.getItem(PROFILE_KEY) || '{}'); } catch { return {}; }
};

export const useTooltip = (id) => {
  const [dismissed, setDismissed] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(TOOLTIPS_KEY) || '{}');
      return Boolean(stored[id]);
    } catch {
      return false;
    }
  });

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      const stored = JSON.parse(localStorage.getItem(TOOLTIPS_KEY) || '{}');
      localStorage.setItem(TOOLTIPS_KEY, JSON.stringify({ ...stored, [id]: true }));
    } catch {
      // no-op
    }
  }, [id]);

  return { show: !dismissed, content: TOOLTIP_CONTENT[id], dismiss };
};

export const OnboardingModal = ({ onFinish }) => {
  const { show, step, currentStep, next, finish, totalSteps } = useOnboarding();
  const [firstName, setFirstName] = useState('');
  const [businessType, setBusinessType] = useState('');

  if (!show) return null;

  const Icon = currentStep.icon;
  const progress = ((step + 1) / totalSteps) * 100;
  const savedProfile = getUserProfile();

  const handleFinish = () => {
    finish();
    onFinish?.();
  };

  const handleNext = () => {
    if (currentStep.isProfile) {
      const profile = { firstName: firstName.trim(), businessType };
      if (profile.firstName || profile.businessType) {
        localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
      }
    }
    next();
  };

  // Personalise welcome title if name already saved or entered
  const displayTitle = currentStep.id === 'welcome' && savedProfile.firstName
    ? `Welcome back, ${savedProfile.firstName}`
    : currentStep.title;
  const displaySubtitle = currentStep.id === 'welcome' && savedProfile.businessType
    ? `Here's your ${savedProfile.businessType.toLowerCase()} command center. Let's get you set up.`
    : currentStep.subtitle;

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-start justify-center z-[100] p-4 overflow-y-auto">
      <div
        className="relative bg-slate-900 border border-white/[0.08] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden my-auto"
        style={{ boxShadow: '0 40px 100px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)' }}
      >
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-slate-800">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {currentStep.skip && (
          <button
            onClick={handleFinish}
            className="absolute top-4 right-4 text-slate-600 hover:text-slate-400 transition-colors z-10"
            aria-label="Close onboarding"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        <div className="p-8 pt-10">
          <div className="flex items-center gap-1.5 mb-6">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all duration-300 ${
                  i === step ? 'w-6 bg-indigo-400' : i < step ? 'w-3 bg-indigo-600' : 'w-3 bg-slate-700'
                }`}
              />
            ))}
          </div>

          <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${currentStep.iconColor} mb-5 shadow-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>

          <h2 className="text-xl font-bold text-white mb-2">{displayTitle}</h2>
          <p className="text-sm text-slate-400 mb-6 leading-relaxed">{displaySubtitle}</p>

          {currentStep.description && (
            <div className="space-y-2 mb-6">
              {currentStep.description.map((item, i) => {
                const ItemIcon = item.icon;
                return (
                  <div key={i} className="flex items-center gap-3 bg-slate-800/60 border border-white/[0.06] rounded-xl p-3">
                    <div className="bg-slate-700/60 p-1.5 rounded-lg flex-shrink-0">
                      <ItemIcon className="w-4 h-4 text-slate-300" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{item.label}</p>
                      <p className="text-xs text-slate-500">{item.sub}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {currentStep.prompts && (
            <div className="space-y-2 mb-6">
              {currentStep.prompts.map((prompt, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-800/40 border border-white/[0.05] rounded-lg px-3 py-2.5">
                  <Sparkles className="w-3 h-3 text-cyan-400 flex-shrink-0" />
                  <p className="text-sm text-slate-300 italic">"{prompt}"</p>
                </div>
              ))}
            </div>
          )}

          {currentStep.isProfile && (
            <div className="mb-6 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2 block">First name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="e.g. Alex"
                  className="w-full px-4 py-3 bg-slate-800 border border-white/[0.08] rounded-xl text-white text-sm placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2 block">What best describes your work?</label>
                <div className="grid grid-cols-3 gap-2">
                  {BUSINESS_TYPES.map(({ label, emoji }) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setBusinessType(label)}
                      className={`py-2.5 px-2 rounded-xl border text-xs font-semibold transition-all flex flex-col items-center gap-1 ${
                        businessType === label
                          ? 'border-indigo-500/60 bg-indigo-500/15 text-white'
                          : 'border-white/[0.06] bg-slate-800/40 text-slate-400 hover:border-white/20 hover:text-white'
                      }`}
                    >
                      <span className="text-base">{emoji}</span>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {currentStep.isExtension && (
            <div className="mb-6 space-y-3">
              <div className="bg-amber-500/8 border border-amber-500/20 rounded-xl p-4">
                <div className="flex items-start gap-2 mb-3">
                  <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-300 leading-relaxed">
                    This extension is <strong>completely optional</strong>. CENTRIS works great without it. It only adds automatic tracking of which subscription sites you visit.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                {[
                  { num: '1', text: 'Install from the Chrome Web Store' },
                  { num: '2', text: 'Go to Subscriptions and generate a pairing code' },
                  { num: '3', text: 'Enter the code in the extension popup' },
                ].map((item) => (
                  <div key={item.num} className="flex items-center gap-3 bg-slate-800/40 border border-white/[0.05] rounded-lg px-3 py-2.5">
                    <span className="w-5 h-5 rounded-full bg-slate-700 text-slate-400 text-xs font-bold flex items-center justify-center flex-shrink-0">{item.num}</span>
                    <p className="text-sm text-slate-300">{item.text}</p>
                  </div>
                ))}
              </div>

              <a
                href="https://chrome.google.com/webstore"
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-white/[0.08] bg-slate-800/40 text-sm text-slate-300 hover:text-white hover:border-white/20 transition-all"
              >
                <Chrome className="w-4 h-4" />
                Open Chrome Web Store
                <ExternalLink className="w-3 h-3 text-slate-500" />
              </a>
            </div>
          )}

          <button
            onClick={step === totalSteps - 1 ? handleFinish : handleNext}
            className="w-full py-3 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:shadow-lg hover:shadow-indigo-500/20 transition-all flex items-center justify-center gap-2"
          >
            {currentStep.cta}
            {step < totalSteps - 1 && <ArrowRight className="w-4 h-4" />}
            {step === totalSteps - 1 && <Check className="w-4 h-4" />}
          </button>

          {currentStep.skip && step < totalSteps - 1 && (
            <button
              onClick={handleNext}
              className="w-full mt-2 py-2 text-xs text-slate-600 hover:text-slate-400 transition-colors"
            >
              Skip for now
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const FeatureTooltip = ({ id }) => {
  const { show, content, dismiss } = useTooltip(id);
  if (!show || !content) return null;

  return (
    <div className="relative z-20">
      <div
        className="absolute left-0 right-0 bg-slate-900 border border-indigo-500/25 rounded-xl shadow-2xl p-4 mt-2"
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-slate-600 hover:text-slate-400 transition-colors"
          aria-label="Dismiss help tooltip"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        {content.optional && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-medium mb-3">
            <Info className="w-2.5 h-2.5" />
            Optional feature
          </span>
        )}

        <p className="text-xs font-semibold text-white mb-3">{content.title}</p>

        <div className="space-y-2 mb-3">
          {content.steps.map((step, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="w-4 h-4 rounded-full bg-indigo-500/20 text-indigo-400 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                {i + 1}
              </span>
              <p className="text-xs text-slate-300 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>

        {content.optionalNote && (
          <div className="bg-amber-500/8 border border-amber-500/15 rounded-lg p-2.5 mb-3">
            <p className="text-xs text-amber-300 leading-relaxed">{content.optionalNote}</p>
          </div>
        )}

        {content.note && (
          <p className="text-xs text-slate-500 leading-relaxed border-t border-white/[0.05] pt-2.5">
            Tip: {content.note}
          </p>
        )}

        <button
          onClick={dismiss}
          className="mt-3 text-xs text-indigo-400 hover:text-indigo-300 transition-colors font-medium"
        >
          Got it, do not show again
        </button>
      </div>
    </div>
  );
};

export const HelpBadge = ({ id }) => {
  const [open, setOpen] = useState(false);
  const content = TOOLTIP_CONTENT[id];
  if (!content) return null;

  return (
    <div className="relative inline-block">
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        onKeyDown={(e) => e.key === 'Enter' && setOpen((o) => !o)}
        className={`w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center transition-colors cursor-pointer select-none ${
          open ? 'bg-indigo-500 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600 hover:text-white'
        }`}
        aria-label="Open help"
      >
        ?
      </span>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute left-0 top-6 w-72 bg-slate-900 border border-white/[0.1] rounded-xl shadow-2xl p-4 z-20"
            style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 text-slate-600 hover:text-slate-400"
              aria-label="Close help"
            >
              <X className="w-3 h-3" />
            </button>

            {content.optional && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-medium mb-3">
                <Info className="w-2.5 h-2.5" /> Optional
              </span>
            )}

            <p className="text-xs font-semibold text-white mb-3">{content.title}</p>

            <div className="space-y-2 mb-3">
              {content.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="w-4 h-4 rounded-full bg-indigo-500/20 text-indigo-400 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                  <p className="text-xs text-slate-300 leading-relaxed">{step}</p>
                </div>
              ))}
            </div>

            {content.optionalNote && (
              <div className="bg-amber-500/8 border border-amber-500/15 rounded-lg p-2.5 mb-2">
                <p className="text-xs text-amber-300 leading-relaxed">{content.optionalNote}</p>
              </div>
            )}

            {content.note && (
              <p className="text-xs text-slate-500 border-t border-white/[0.05] pt-2.5 leading-relaxed">
                Tip: {content.note}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export const SetupChecklist = ({ expenses, invoices, extensionPaired, onNavigate }) => {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(CHECKLIST_KEY) === 'done'
  );

  const items = [
    {
      id: 'expense',
      label: 'Import your first expense',
      sub: 'Upload a bank statement (Subscriptions) or scan a receipt (Expenses)',
      done: (expenses?.length || 0) > 0,
      action: 'Go to Expenses',
      view: 'expenses',
    },
    {
      id: 'invoice',
      label: 'Create an invoice',
      sub: 'Send a professional invoice to your first client',
      done: (invoices?.length || 0) > 0,
      action: 'Create Invoice',
      view: 'business-tools',
    },
    {
      id: 'extension',
      label: 'Pair Chrome extension',
      sub: 'Optional - auto-tracks subscription site visits',
      done: Boolean(extensionPaired),
      action: 'Set Up',
      view: 'subscriptions',
      optional: true,
    },
  ];

  const completed = items.filter((i) => i.done).length;
  const allDone = completed === items.length;

  const handleDismiss = useCallback(() => {
    localStorage.setItem(CHECKLIST_KEY, 'done');
    setDismissed(true);
  }, []);

  useEffect(() => {
    if (allDone) {
      const t = setTimeout(handleDismiss, 3000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [allDone, handleDismiss]);

  if (dismissed) return null;

  return (
    <div className="centris-card flex items-center gap-3 px-3 py-2 rounded-xl">
      <p className="text-[11px] text-slate-500 shrink-0">{completed}/{items.length} setup</p>
      <div className="flex-1 flex items-center gap-2">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => !item.done && onNavigate?.(item.view)}
            className={`flex items-center gap-1 text-[11px] transition-colors ${item.done ? 'text-emerald-400' : 'text-indigo-400 hover:text-indigo-300'}`}
          >
            {item.done ? <Check className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
            <span className={item.done ? 'line-through opacity-60' : ''}>{item.label.replace('Import your first expense', 'Add expense').replace('Create an invoice', 'Invoice').replace('Pair Chrome extension', 'Extension')}</span>
          </button>
        ))}
      </div>
      <button onClick={handleDismiss} className="text-slate-600 hover:text-slate-400 shrink-0">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default OnboardingModal;
